import qs from 'qs';
import { axiosClient } from '@/libs/axios-client';
import {
  prepareFilters,
  preparePagination,
  prepareSorters,
} from '@/utils/apiParamsUtils';
import { getMonthName, getYear } from '@/utils/dateUtils';

/**
 * Creates a new salary with the provided data.
 * @param {Object} payload - The data for the new salary.
 * @returns {Promise<Object>} A promise that resolves with the created salary data.
 * @throws Will throw an error if the creation request fails.
 */
export const createNewSalary = async (payload) => {
  try {
    const isFormData = payload instanceof FormData;

    const response = await axiosClient.post('/salaries/create', payload, {
      headers: isFormData ? {} : { 'Content-Type': 'application/json' },
    });

    return response.data;
  } catch (error) {
    console.error('Failed to create a new salary:', error);
    throw error;
  }
};

/**
 * @typedef {Object} SalaryParams
 * @property {string} company - The ID of the company the employee belongs to.
 * @property {number} [month] - The month for which salary data is requested (optional).
 * @property {number} [year] - The year for which salary data is requested (optional).
 * @property {number} [offset] - The offset for pagination (optional).
 * @property {number} [limit] - The number of results to return (optional).
 */

/**
 * Retrieves all salaries based on provided filters.
 *
 * @param {SalaryParams} params - Query parameters for filtering salary data.
 * @returns {Promise<Object[]>} A promise that resolves with an array of salary objects.
 * @throws Will throw an error if the request fails.
 */
export const retrieveAllSalaries = async (params) => {
  const filters = prepareFilters(params, {
    company: 'default',
    month: 'default',
    year: 'default',
    firstName: 'default',
    lastName: 'default',
    personalNo: 'default',
    status: 'uppercaseArray',
    // Employment-type filter (case-sensitive array → backend $in) and the
    // include-non-employment checkbox; both are read from the `filter` JSON by
    // the timesheet service.
    'employmentSettings.employmentType': 'default',
    includeNonEmployment: 'default',
    // Marker filter: keep only rows with an unapproved leave request (⚠️) this
    // month. Read from the `filter` JSON by the timesheet service.
    hasPendingLeave: 'default',
  });

  const sorters = prepareSorters(params, {
    firstName: 'sortFirstName',
    lastName: 'sortLastName',
    status: 'sortStatus',
    updatedAt: 'sortUpdatedAt',
  });

  const pagination = preparePagination(params);

  try {
    const response = await axiosClient.get('/salaries-timesheet', {
      params: {
        filter: {
          ...filters,
          month: getMonthName(filters.month),
          year: getYear(filters.year),
        },
        sorter: sorters,
        ...pagination,
      },
      paramsSerializer: (params) => {
        if (params.filter) {
          params.filter = JSON.stringify(params.filter);
        }

        if (params.sorter) {
          params.sorter = JSON.stringify(params.sorter);
        }

        return qs.stringify(params, { encode: false, arrayFormat: 'brackets' });
      },
    });

    return response.data;
  } catch (error) {
    console.error('Failed to retrieve salaries:', error);
    throw error;
  }
};

/**
 * @typedef {Object} EmployeeSalaryParams
 * @property {string} employeeId - The ID of the employee whose salary data is being retrieved.
 * @property {string} company - The ID of the company the employee belongs to.
 * @property {number} [month] - The month for which salary data is requested (optional).
 * @property {number} [year] - The year for which salary data is requested (optional).
 */

/**
 * Retrieves salary data for a specific employee within a company.
 *
 * @param {EmployeeSalaryParams} params - Query parameters for filtering salary data.
 * @returns {Promise<Object>} A promise that resolves with the salary data for the specified employee.
 * @throws Will throw an error if the request fails.
 */
export const retrieveEmployeeSalary = async (params) => {
  try {
    const response = await axiosClient.get('/salaries-timesheet/employee', {
      params: {
        employeeId: params.employeeId,
        companyId: params.company,
        month: getMonthName(params.month),
        year: getYear(params.year),
      },
      paramsSerializer: (params) => {
        return qs.stringify(params, { encode: false, arrayFormat: 'brackets' });
      },
    });

    return response.data;
  } catch (error) {
    console.error('Failed to retrieve employee salary:', error);
    throw error;
  }
};

/**
 * Updates an existing salary by their ID with the provided data.
 * @param {string} salaryId - The unique identifier of the salary.
 * @param {Object} payload - The updated data for the salary.
 * @returns {Promise<Object>} A promise that resolves with the updated salary data.
 * @throws Will throw an error if the update request fails.
 */
export const updateSalaryById = async (salaryId, payload) => {
  try {
    const isFormData = payload instanceof FormData;

    const response = await axiosClient.put(`/salaries/${salaryId}`, payload, {
      headers: isFormData ? {} : { 'Content-Type': 'application/json' },
    });

    return response.data;
  } catch (error) {
    console.error(
      `Failed to update salary with the salary ID ${salaryId}:`,
      error
    );
    throw error;
  }
};

/**
 * Creates an initial salary entry with the provided data.
 * @param {Object} payload - The data required to create the initial salary.
 * @returns {Promise<Object>} A promise that resolves with the created salary data.
 * @throws Will throw an error if the creation request fails.
 */
export const createInitialSalary = async (payload) => {
  try {
    const response = await axiosClient.post(
      '/salaries/create-initial',
      payload
    );
    return response.data;
  } catch (error) {
    console.error('Failed to create initial salary:', error);
    throw error;
  }
};

/**
 * Creates multiple initial salary entries in bulk with the provided data.
 * @param {Array<Object>} payload - An array of data objects to create initial salaries.
 * @returns {Promise<Object>} A promise that resolves with the result of the bulk creation.
 * @throws Will throw an error if the bulk creation request fails.
 */
export const createInitialSalariesBulk = async (payload) => {
  try {
    const response = await axiosClient.post(
      '/salaries/create-initial-bulk',
      payload
    );
    return response.data;
  } catch (error) {
    console.error('Failed to create initial salaries in bulk:', error);
    throw error;
  }
};

/**
 * Uploads a timesheet Excel file and related metadata to the server to create salaries in bulk.
 *
 * Sends a POST request to `/salaries/upload` (expects `multipart/form-data`) with:
 * * `file` (Blob/File) — the Excel workbook,
 * * `company` (string ObjectId),
 * * `month` (number 1–12),
 * * `year` (number, e.g., 2025),
 * * `platform` (string, e.g., "HLB").
 *
 * @param {FormData|Object} formData - FormData containing the file and fields listed above.
 * @returns {Promise<Object>} Resolves with the API response (e.g., `{ message, created, warnings }`).
 * @throws {Error} Re-throws if the upload request fails.
 */
export const uploadSalaries = async (formData) => {
  try {
    const response = await axiosClient.post('/salaries/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });

    return response.data;
  } catch (error) {
    console.error('Failed to upload salaries:', error);
    throw error;
  }
};

/**
 * Uploads a bonuses & deductions Excel file to create or update salaries.
 *
 * @param {FormData} formData - FormData with file, company, month, year.
 * @returns {Promise<Object>} { message, created, updated, warnings }
 */
export const uploadBonuses = async (formData) => {
  try {
    const response = await axiosClient.post(
      '/salaries/upload-bonuses',
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );

    return response.data;
  } catch (error) {
    console.error('Failed to upload bonuses:', error);
    throw error;
  }
};

/**
 * Runs the timesheet review for a given company and period.
 *
 * @param {{ companyId: string, month: string|number, year: string|number }} params
 * @returns {Promise<{ summary: { high:number, medium:number, low:number }, config: object, flags: object[] }>}
 */
export const runTimesheetReview = async ({ companyId, month, year }) => {
  // Accept either a numeric month (1-12) from URL params or an already-resolved
  // month name (e.g. "April") coming from forms. Year is passed through as a
  // string. Service does not double-convert when caller already normalised.
  const monthName =
    typeof month === 'number' || /^\d+$/.test(String(month || ''))
      ? getMonthName(Number(month))
      : month;
  const yearStr = String(year);
  try {
    const response = await axiosClient.get('/salaries-timesheet/review', {
      params: {
        companyId,
        month: monthName,
        year: yearStr,
      },
      paramsSerializer: (params) =>
        qs.stringify(params, { encode: false, arrayFormat: 'brackets' }),
    });

    return response.data;
  } catch (error) {
    console.error('Failed to run timesheet review:', error);
    throw error;
  }
};

/**
 * Retrieve bulk timesheet overview (multi-employee, multi-month).
 * Backend returns 422 with OVERVIEW_RANGE_TOO_LARGE if cells > 1200.
 * Backend returns 429 if more than 2 concurrent overview requests per user.
 */
export const retrieveTimesheetOverview = async (params) => {
  try {
    const response = await axiosClient.get('/salaries-timesheet/overview', {
      params,
      paramsSerializer: (p) =>
        qs.stringify(p, { encode: false, arrayFormat: 'brackets', skipNulls: true }),
    });
    return response.data;
  } catch (error) {
    console.error('Failed to retrieve timesheet overview:', error);
    throw error;
  }
};

/**
 * Retrieve detailed info for a single salary day (cell popover lazy fetch).
 */
export const retrieveSalaryDayDetails = async ({ salaryId, date }) => {
  try {
    const response = await axiosClient.get(
      `/salaries-timesheet/day/${salaryId}/${date}`
    );
    return response.data;
  } catch (error) {
    console.error('Failed to retrieve salary day details:', error);
    throw error;
  }
};

const SalariesService = {
  retrieveAllSalaries,
  retrieveEmployeeSalary,
  createNewSalary,
  updateSalaryById,
  createInitialSalary,
  createInitialSalariesBulk,
  uploadSalaries,
  uploadBonuses,
  runTimesheetReview,
  retrieveTimesheetOverview,
  retrieveSalaryDayDetails,
};

export default SalariesService;
